import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, KeyRound, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

function errMsg(d) {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
  return "Bir hata oluştu.";
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("email"); // email | code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState("");

  const sendEmail = async () => {
    setLoading(true); setInfo("");
    try {
      const d = await api.forgotPassword(email.trim(), "email");
      setInfo(d.message || "İşlem tamamlandı.");
    } catch (e) {
      toast.error(errMsg(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const resetByCode = async () => {
    if (pw.length < 6) { toast.error("Yeni şifre en az 6 karakter olmalı"); return; }
    if (pw !== pw2) { toast.error("Şifreler eşleşmiyor"); return; }
    setLoading(true);
    try {
      await api.resetWithCode(email.trim(), code.trim(), pw);
      toast.success("Şifreniz sıfırlandı, giriş yapabilirsiniz");
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(errMsg(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="relative w-full max-w-md">
        <Link to="/login" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Girişe dön</Link>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-2xl">
          <h1 className="mb-1 font-display text-2xl font-bold">Şifremi Unuttum</h1>
          <p className="mb-6 text-sm text-muted-foreground">E-posta ile sıfırlama bağlantısı alın veya kurtarma kodunuzla yeni şifre belirleyin.</p>

          <div className="mb-6 grid grid-cols-2 gap-2">
            <button data-testid="fp-mode-email" onClick={() => setMode("email")} className={`flex items-center justify-center gap-1.5 rounded-lg border py-2.5 text-sm font-medium ${mode === "email" ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"}`}><Mail className="h-4 w-4" /> E-posta</button>
            <button data-testid="fp-mode-code" onClick={() => setMode("code")} className={`flex items-center justify-center gap-1.5 rounded-lg border py-2.5 text-sm font-medium ${mode === "code" ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"}`}><KeyRound className="h-4 w-4" /> Kurtarma Kodu</button>
          </div>

          <div className="space-y-4">
            <div><Label>E-posta</Label><Input data-testid="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ornek@arslan.ro" className="mt-1.5" /></div>

            {mode === "email" ? (
              <>
                {info && <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">{info}</div>}
                <Button data-testid="fp-send-email" onClick={sendEmail} disabled={loading || !email} className="w-full">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sıfırlama Bağlantısı Gönder"}</Button>
              </>
            ) : (
              <>
                <div><Label>Kurtarma Kodu</Label><Input data-testid="fp-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXX-XXXX-XXXX" className="mt-1.5 font-mono" /></div>
                <div><Label>Yeni Şifre</Label><Input data-testid="fp-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5" /></div>
                <div><Label>Yeni Şifre (Tekrar)</Label><Input data-testid="fp-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1.5" /></div>
                <Button data-testid="fp-reset-code" onClick={resetByCode} disabled={loading || !email || !code} className="w-full">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Şifreyi Sıfırla"}</Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
